<template>
  <div class="container-position">
    <div v-if="required" class="text-muted small optional-label">Optional</div>
    <div :class="[{ 'quill--mini': mini }]" @click.stop="() => {}">
      <div ref="toolbar" />
      <div ref="editor" />
    </div>
  </div>
</template>

<script>
import Quill from 'quill';

export default {
  props: {
    mini: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    value: {
      type: String,
      default: null
    },
    selectedTemplate: {
      type: String,
      default: null
    },
    toolbar: {
      type: Array,
      default: null
    },
    // Must cover every format `toolbar` offers — Quill silently disables buttons it doesn't list.
    formats: {
      type: Array,
      default: () => [
        'bold',
        'italic',
        'underline',
        'strike',
        'color',
        'background',
        'header',
        'list',
        'bullet',
        'align',
        'link',
        'code-block',
        'image'
      ]
    },
  },
  data() {
    return {
      quill: null,
      tempImageId: null,
      tempImageIndex: null,
      upload: {
        bucket: 'APP',
        folderId: null,
        folderName: 'quillFiles'
      }
    };
  },
  mounted() {
    this.upload.folderId = this.loggedInUser?._id ? this.loggedInUser._id : null;
    this.$nextTick(() => {
      this.registerCustomBlots();
      this.initializeQuill();
      this.updateEditorContent(this.value);
      this.quill.root.addEventListener('paste', this.handleImagePaste);
    });
  },
  beforeDestroy() {
    if (this._tempBlobURL) {
      URL.revokeObjectURL(this._tempBlobURL);
      this._tempBlobURL = null;
    }
    if (this.quill) {
      if (this._textChangeHandler) {
        this.quill.off('text-change', this._textChangeHandler);
        this._textChangeHandler = null;
      }
      this.quill.root.removeEventListener('paste', this.handleImagePaste);
      this.quill = null;
    }
  },
  methods: {
    sanitizeContent(content) {
      return content?.replace(/<undefined>|<\/undefined>/g, '');
    },
    preprocessOutput(html) {
      return html?.replace(/<p>/g, '<div>')?.replace(/<\/p>/g, '</div>');
    },
    updateEditorContent(content, force = false) {
      if (content || force) {
        const sanitizedValue = this.sanitizeContent(content);
        const formattedHtml = this.preprocessOutput(sanitizedValue);
        this.quill.clipboard.dangerouslyPasteHTML(formattedHtml);
      }
    },
    registerCustomBlots() {
      const Block = Quill.import('blots/block');
      let BlockEmbed = Quill.import('blots/block/embed');

      class DivBlock extends Block {
        static blotName = 'div';
        static tagName = 'div';
      }

      class ImageBlot extends BlockEmbed {
        static create({ url = '', alt, style, width, height = '100%', loading, dataTempId }) {
          const node = super.create();
          node.setAttribute('src', url);
          alt && node.setAttribute('alt', alt);
          style && node.setAttribute('style', style);
          width && node.setAttribute('width', width);
          node.setAttribute('height', height);
          loading && node.setAttribute('loading', loading);
          dataTempId && node.setAttribute('data-temp-id', dataTempId)
    
          return node;
        }

        static value(node) {
          return {
            alt: node.getAttribute('alt'),
            url: node.getAttribute('src'),
            width: node.getAttribute('width'),
            height: node.getAttribute('height'),
            style: node.getAttribute('style'),
            loading: node.getAttribute('loading'),
            dataTempId: node.getAttribute('data-temp-id')
          };
        }
      }
      ImageBlot.blotName = 'image';
      ImageBlot.tagName = 'img';

      Quill.register(DivBlock, true);
      Quill.register(ImageBlot);
    },
    initializeQuill() {
      if (this.quill || !this.$refs.editor) {
        return;
      }
      const toolbarOptions = this.toolbar || [
        [{ font: [] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image'],
        ['code-block'],
        ['clean']
      ];

      this.quill = new Quill(this.$refs.editor, {
        modules: {
          toolbar: {
            container: toolbarOptions,
            handlers: {
              image: this.imageHandler.bind(this),
            }
          },
          clipboard: {
            matchVisual: false
          }
        },
        formats: this.formats,
        placeholder: 'Enter text...',
        theme: 'snow'
      });

      this.quill.getModule('toolbar').container = this.$refs.toolbar;

      this._textChangeHandler = (delta, oldDelta, source) => {
        if (source === 'user') {
          this.updateHtml();
        }
        if (source === 'api'){
          for (let op of delta.ops) {
            if (op.insert?.image && op.insert?.image?.url) {
              this.updateHtml();
              break;
            }
          }
        }
      };
      this.quill.on('text-change', this._textChangeHandler);
    },
    updateHtml(){
      let html = this.quill.root.innerHTML;
      html = this.preprocessOutput(html);
      this.$emit('input', html);
    },
    async handleImagePaste(event) {
      const clipboardData = event.clipboardData || window.clipboardData;
      const items = clipboardData.items;
      const item = items[0];
      if (item && item.type.indexOf('image') !== -1) {
        event.preventDefault();
        const file = item.getAsFile();
        await this.imageInsert(file);
      }
    },
    imageHandler() {
      const input = this.createFileInput();
      input.click();

      input.onchange = async () => {
        const file = input.files[0];
        await this.imageInsert(file);
      };
    },
    async imageInsert(file){
      if (file) {
        const formData = this.prepareImageFormData(file);
        this.quill.focus();
        const rangeIndex = (this.quill.getSelection()?.index);

        // Insert temporary image and get index
        const tempImage = this.createTempImageURL(file);
        this.quill.insertEmbed(rangeIndex, 'image', tempImage);

        // Upload the image and handle the result
        const images = await this.uploadImage(formData);
        this.handleImageUploadResult(images, rangeIndex);
      }
    },
    createFileInput() {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      return input;
    },
    prepareImageFormData(file) {
      const formData = new FormData();
      formData.append('files', file);
      formData.append('mediaType', 'Image');
      formData.append('bucket', this.upload.bucket);
      this.upload?.folderId && formData.append('folderId', this.upload.folderId);
      formData.append('folderName', this.upload.folderName);
      return formData;
    },
    createTempImageURL(file) {
      this.tempImageId = new Date().getTime();
      const url = URL.createObjectURL(file);
      this._tempBlobURL = url;
      return { url, style: 'opacity: 0.5', dataTempId: this.tempImageId };
    },
    handleImageUploadResult(images, imageIndex) {
      this.getTempImageIndex();

      if (this._tempBlobURL) {
        URL.revokeObjectURL(this._tempBlobURL);
        this._tempBlobURL = null;
      }

      if (images && images.length) {
        const { location } = images[0];
        const embed = { url: location, loading: 'lazy', style: 'max-width: 100%'};
        this.quill.deleteText(this.tempImageIndex, 1);
        this.quill.insertEmbed(imageIndex, 'image', embed);
      } else {
        this.quill.deleteText(imageIndex, 1);
      }
    },
    getTempImageIndex(){
      let totalLength = this.quill.getLength();
      for (let i = 0; i < totalLength; i++) {
        let format = this.quill.getContents(i, 1);
        if (format?.ops[0]?.insert?.image?.dataTempId === String(this.tempImageId)) {
          this.tempImageIndex = i;
          break;
        }
      }
    },
    async uploadImage(formData) {
      try {
        return (await this.$http.post('/upload', formData)).data;
      } catch (e) {
        this.notifyError('Failed to save image');
        return null;
      }
    },
  },
  watch: {
    selectedTemplate(newVal) {
      if (this.quill && this.quill.root.innerHTML !== this.preprocessOutput(this.sanitizeContent(newVal))) {
        this.updateEditorContent(newVal);
      }
    }
  }
};
</script>

<style scoped>
::v-deep .ql-toolbar {
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  background-color: #ffffff;
}

.optional-label {
  position: absolute;
  top: -35px;
  right: 0;
  margin: 10px;
}
.container-position {
  position: relative;
}
::v-deep .ql-container {
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  font-size: inherit !important;
  background-color: #ffffff;
}

::v-deep .ql-editor {
  min-height: 150px;
}

::v-deep .ql-picker.ql-expanded .ql-picker-options {
  z-index: 10;
}
::v-deep input.ql-image {
  display: none;
}
</style>
